<template>
    <el-select v-model="selectedDepartment" :placeholder="placeholder" @change="handleChange" :disabled="disabled">
        <el-option v-if="isSearchModel" label="すべて" :value="ALL" />
        <el-option v-for="item in filteredDepartments" :key="item.department_id" :label="item.name"
            :value="item.department_id" />
    </el-select>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, computed } from 'vue';
import axios from 'axios';
import { ALL } from '@/config/constants';

export interface Department {
    department_id: string;
    name: string;
    organization_id: string;
    organization_name: string;
}

export default defineComponent({
    name: 'DepartmentSelect',
    props: {
        isSearch: {
            type: Boolean,
            default: false,
        },
        organizationId: {
            type: String,
            required: true,
        },
        modelValue: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: '选择部门',
        },
        isExcludeTargetDepartment: {
            type: Boolean,
            default: false,
        },
        targetDepartmentId: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue', 'change', 'department-name-change', 'organization-id-change'],
    setup(props, { emit }) {
        const allDepartments = ref<Department[]>([]);
        
        // Computed property to filter departments
        const filteredDepartments = computed(() => {
            console.log('Filtering departments:', {
                isExcludeTargetDepartment: props.isExcludeTargetDepartment,
                targetDepartmentId: props.targetDepartmentId,
                allDepartments: allDepartments.value.length
            });
            
            if (!props.isExcludeTargetDepartment || !props.targetDepartmentId) {
                return allDepartments.value;
            }
            
            const filtered = allDepartments.value.filter(dept => dept.department_id !== props.targetDepartmentId);
            console.log('Filtered departments:', filtered.length);
            return filtered;
        });

        // 使用 computed 同步外部的 modelValue
        const selectedDepartment = computed<string>({
            get() {
                return props.modelValue;
            },
            set(val: string) {
                emit('update:modelValue', val);
                emit('change', val);
            }
        });
        const isSearchModel = ref(props.isSearch);

        // 加载部门数据
        const fetchDepartments = async () => {
            try {
                const response = await axios.get('/api/departments/all', {
                    params: {
                        organization_id: props.organizationId === ALL ? '' : props.organizationId,
                    },
                });
                allDepartments.value = response.data.data || [];
            } catch (error) {
                console.error('获取部门列表失败:', error);
            }
        };

        const handleChange = (value: string) => {
            // 找到选中的部门对象
            const selectedDepartmentObj = filteredDepartments.value.find(
                dept => dept.department_id === value
            );
            // 如果找到了部门对象，发送名称
            if (selectedDepartmentObj) {
                emit('department-name-change', selectedDepartmentObj.name);
                emit('organization-id-change', selectedDepartmentObj.organization_id);
            }
        };

        // 初次加载：如果传入的 organizationId 有值则加载数据
        onMounted(() => {
            // if (props.organizationId) {
            fetchDepartments();
            // }
        });

        watch(
            () => props.organizationId,
            async (newOrgId, oldOrgId) => {
                if (newOrgId === oldOrgId) return;
                // 清空当前部门数据
                allDepartments.value = [];
                // 加载新组织对应的部门数据
                if (newOrgId) {
                    await fetchDepartments();
                    // 检查当前 modelValue 是否在新部门列表中存在
                    if (props.modelValue) {
                        const exists = filteredDepartments.value.some(
                            dept => dept.department_id === props.modelValue
                        );
                        if (!exists) {
                            // 如果不存在，清空选中值
                            emit('update:modelValue', isSearchModel.value ? ALL : '');
                        }
                    }
                }
            }
        );


        // watch(() => props.modelValue, (newValue) => {
        //     selectedDepartment.value = newValue;
        // });

        return {
            filteredDepartments, // Use this instead of departments
            selectedDepartment,
            handleChange,
            isSearchModel,
            ALL
        };
    },
});
</script>